import React, { useState, useEffect, useRef } from 'react';
import { styled } from '@mui/system';
import { useParams } from 'react-router-dom';
import { useServices } from '../../services';
import CircularProgress from 'components/core/CircularProgress';
import NotFoundContent from 'components/core/NotFoundContent';
import PageContent from 'components/core/PageContent';

const IframeWrapper = styled('div')({
  flex: '1 1 auto',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: 'calc(100vh - 120px)',
  paddingTop: '3rem',
});

const StyledIframe = styled('iframe')({
  flex: 1,
  width: '100%',
  border: 'none',
});

const LoadingWrapper = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: 'calc(100vh - 120px)',
});

export interface DocsEmbedProps {
  /** Static path appended after the docs base URL, e.g. "platform_community/about/".
   *  If omitted, the React Router wildcard ("*") is used instead, enabling
   *  deep-linking: /help/for_data_users/using_notebooks/ → for_data_users/using_notebooks/ */
  path?: string;
}

export default function DocsEmbed({ path }: DocsEmbedProps): React.ReactElement {
  const { envService } = useServices();
  const { '*': wildcardPath } = useParams();
  const docsUrl = envService.getValue('DOCS_URL') ?? 'https://msd-live.github.io/msdlive-docs';
  const resolvedPath = path ?? wildcardPath ?? '';

  // Pass parent origin to the iframe so postMessage can target it explicitly
  const parentOrigin = encodeURIComponent(window.location.origin);
  const embedUrl = `${docsUrl}/embed/${resolvedPath}?parentOrigin=${parentOrigin}`;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const openInNewTab = (url: string): void => {
  const a = document.createElement('a');
  a.href = url;
  a.target = '_blank';
  a.rel = 'noopener noreferrer';
  a.click();
};

  useEffect((): (() => void) => {
  const docsOrigin = new URL(docsUrl).origin;

  const handleMessage = (event: MessageEvent): void => {
  if (event.origin !== docsOrigin) return;

  if (event.data?.type === 'docs-navigate') {
    const docPathname: string = event.data.pathname; // e.g. /embed/platform_community/about/
    // Strip /embed prefix and open on the docs site
    const strippedPathname = docPathname.replace(/^.*\/embed/, '');
    openInNewTab(`${docsUrl}${strippedPathname}`);
  }
};

  window.addEventListener('message', handleMessage);
  return (): void => window.removeEventListener('message', handleMessage);
}, [docsUrl]);

  if (error) {
    return (
      <PageContent>
        <NotFoundContent />
      </PageContent>
    );
  }

  return (
    <IframeWrapper>
      {loading && (
        <LoadingWrapper>
          <CircularProgress text="Loading documentation..." />
        </LoadingWrapper>
      )}
      <StyledIframe
        src={embedUrl}
        title="MSD-LIVE Documentation"
        style={loading ? { display: 'none' } : undefined}
        onLoad={() => setLoading(false)}
        onError={() => {
          setLoading(false);
          setError(true);
        }}
      />
    </IframeWrapper>
  );
}